/** Commission calls. Shapes copied from workers/api/src/commission/routes.ts on `main`. */
import type { ApiClient } from "./client";

export const TRIGGERS = ["pt_session_delivered", "payment_collected", "plan_sold", "class_taught", "target_hit"] as const;
export type Trigger = (typeof TRIGGERS)[number];
export const ITEM_KINDS = ["membership", "pt_pack", "class_pack", "service", "product", "fee", "combo", "class"] as const;
export type SaleType = "new" | "renewal";

export type RuleWho = { kind: "staff"; staffId: string } | { kind: "role"; roleId: string } | { kind: "everyone" };
export type HowMuch =
  | { kind: "percent"; bp: number }
  | { kind: "fixed"; amount: number }
  | { kind: "slabs"; metric: "count" | "amount"; mode: "whole" | "marginal"; bands: Array<{ from: number; rate: unknown }> };
export interface RuleBody {
  when: Trigger; who: RuleWho;
  what: { itemKind?: string; itemIds?: string[]; branchIds?: string[] };
  howMuch: HowMuch;
  conditions?: { saleType?: SaleType; minAmount?: number; perEventCap?: number; monthlyCap?: number };
}
export interface RuleVersion {
  id: string; version: number; name: string; rule: RuleBody; effectiveFrom: string; effectiveTo: string | null;
  priority: number | null; stackable: boolean; createdByStaffId: string | null; createdAt: number;
}
export interface RuleInput { name: string; rule: RuleBody; effectiveFrom: string; priority: number | null; stackable: boolean }

export type LedgerStatus = "approved" | "held" | "released" | "reversed";
export interface LedgerLine {
  id: string; lineId: string; staffId: string; eventId: string; eventKey: string; ruleId: string; ruleVersion: number;
  amount: number; basis: string; periodMonth: string; status: LedgerStatus; reversesLineId: string | null; branchId: string | null; createdAt: number;
}

/** The business's roles (GET /roles, roles:view). Only the fields the rule form needs. */
export interface RoleOption { id: string; name: string }
export const fetchRoles = (c: ApiClient) => c.get<{ roles: RoleOption[] }>("/roles");

export const fetchRules = (c: ApiClient) => c.get<{ rules: RuleVersion[] }>("/commission/rules");
export const createRule = (c: ApiClient, body: RuleInput) => c.post<{ rule: RuleVersion }>("/commission/rules", body);
export const newRuleVersion = (c: ApiClient, id: string, body: RuleInput) => c.post<{ rule: RuleVersion }>(`/commission/rules/${id}/versions`, body);
export const retireRule = (c: ApiClient, id: string, effectiveTo: string) => c.post<{ rule: RuleVersion }>(`/commission/rules/${id}/retire`, { effectiveTo });

export interface LedgerQuery { staffId?: string; month?: string; status?: string; limit: number }
export const fetchLedger = (c: ApiClient, q: LedgerQuery) => c.get<{ lines: LedgerLine[] }>("/commission/ledger", { ...q });
export const releaseLedger = (c: ApiClient) => c.post<{ result: Record<string, unknown> }>("/commission/ledger/release");

export const fetchMyCommission = (c: ApiClient, q: { month?: string; limit: number }) => c.get<{ lines: LedgerLine[]; total: number; held: number }>("/me/commission", { ...q });
